import React from 'react'
import { useTranslation } from 'react-i18next'
import { ShortcutHint } from '@/src/renderer/components/ui/ShortcutHint'
import { CheckCircleFilledIcon, TaskStartIcon, DormantIcon } from '@/src/renderer/components/icons'
import { MiniCalendar } from './MiniCalendar'
import { PeriodOptions } from './PeriodOptions'
import { TimePointRow } from './TimePointRow'
import { WhenDropdownFooter } from './WhenDropdownFooter'
import { TodayIcon, TomorrowIcon, PeriodIcon, ClockIcon } from './WhenDropdownIcons'
import { isToday, isTomorrow } from './whenDropdownUtils'
import { formatStartDate } from '../../utils/parseStartDate'
import { useDateTimeDropdownState } from '../../hooks/useDateTimeDropdownState'
import type { WhenDropdownContentProps } from './WhenDropdown.types'
import './WhenDropdown.scss'

/**
 * WhenDropdownContent — Dropdown for selecting task start date/time.
 *
 * Two-column layout:
 * - Left: Day selection (Today/Tomorrow quick picks + calendar)
 * - Right: Time period or exact time selection
 *
 * Saves only on "Confirm" button click (unlike Attention/Duration which save immediately)
 * — except in `inline` (Aufgaben-Details), where every change is written at once
 * and the footer only offers the way back (ADR 0026).
 */
export const WhenDropdownContent: React.FC<WhenDropdownContentProps> = ({
  value,
  onValueChange,
  onClose,
  onRemove,
  onRest,
  resting = false,
  placement = 'actionbar',
}) => {
  const { t } = useTranslation()
  const inline = placement === 'inline'

  const state = useDateTimeDropdownState({
    value,
    onChange: onValueChange,
    onClose,
    formatDisplay: formatStartDate,
    autoFocus: !inline,
    inline,
  })

  // ── Display icon for NL input ──
  let displayIcon: React.ReactNode = <TaskStartIcon size={16} />
  if (state.timeSource === 'time' && state.selectedTime) displayIcon = <ClockIcon size={16} />
  else if (state.timeSource === 'period' && state.selectedPeriod) displayIcon = <PeriodIcon period={state.selectedPeriod} size={16} />
  else if (state.daySource === 'quick' && state.selectedDate) {
    if (isToday(state.selectedDate)) displayIcon = <TodayIcon size={16} />
    else if (isTomorrow(state.selectedDate)) displayIcon = <TomorrowIcon size={16} />
  }

  return (
    <div className="when-dropdown" data-placement={placement} onKeyDown={state.handleGlobalKeyDown}>
      {/* NL Input */}
      <div className="when-dropdown__search-wrapper">
        <div className="when-dropdown__search">
          {displayIcon}
          <input
            ref={state.inputRef}
            className="when-dropdown__input"
            placeholder={t('taskCreation.actionStart')}
            value={state.inputValue ?? state.displayText}
            onChange={(e) => state.setInputValue(e.target.value)}
            onKeyDown={state.handleInputKeyDown}
            onFocus={() => { if (state.inputValue === null && state.displayText) state.setInputValue(state.displayText) }}
            // In den Aufgaben-Details stehen sieben Inhalte gleichzeitig offen — sechs
            // von ihnen hätten ein `autoFocus`, und der letzte gewänne. Der Fokus gehört
            // dort der Liste (CONTEXT.md, Aufgaben-Details), bis jemand hineingreift.
            autoFocus={!inline}
          />
        </div>
      </div>

      {/* Two-column selection */}
      <div className="when-dropdown__selection">
        <div className="when-dropdown__columns">
          {/* Day Column */}
          <div className="when-dropdown__column when-dropdown__column--day">
            <div className="when-dropdown__column-label">
              {t('taskCreation.whenDayLabel')}
            </div>

            {/* Today */}
            <button
              type="button"
              className={`when-dropdown__item${state.daySource === 'quick' && state.selectedDate && isToday(state.selectedDate) ? ' when-dropdown__item--selected' : ''}`}
              onClick={() => state.handleQuickDay(0)}
              // Der Doppelklick ist „wählen UND bestätigen" — in `inline` hat
              // der erste Klick schon geschrieben, ein zweites Schreiben
              // desselben Werts wäre nur ein weiterer Eintrag in der Warteschlange.
              onDoubleClick={inline ? undefined : () => {
                const d = new Date(); d.setHours(0, 0, 0, 0)
                onValueChange({ date: d, time: null, period: null })
                onClose()
              }}
            >
              <TodayIcon size={16} />
              <span className="when-dropdown__item-text">{t('taskCreation.whenToday')}</span>
              <div className="when-dropdown__item-right">
                {state.daySource === 'quick' && state.selectedDate && isToday(state.selectedDate) && (
                  <CheckCircleFilledIcon size={16} className="when-dropdown__check" />
                )}
                <ShortcutHint>1</ShortcutHint>
              </div>
            </button>

            {/* Tomorrow */}
            <button
              type="button"
              className={`when-dropdown__item${state.daySource === 'quick' && state.selectedDate && isTomorrow(state.selectedDate) ? ' when-dropdown__item--selected' : ''}`}
              onClick={() => state.handleQuickDay(1)}
              onDoubleClick={inline ? undefined : () => {
                const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(0, 0, 0, 0)
                onValueChange({ date: d, time: null, period: null })
                onClose()
              }}
            >
              <TomorrowIcon size={16} />
              <span className="when-dropdown__item-text">{t('taskCreation.whenTomorrow')}</span>
              <div className="when-dropdown__item-right">
                {state.daySource === 'quick' && state.selectedDate && isTomorrow(state.selectedDate) && (
                  <CheckCircleFilledIcon size={16} className="when-dropdown__check" />
                )}
                <ShortcutHint>2</ShortcutHint>
              </div>
            </button>

            {/* Mini Calendar */}
            <MiniCalendar
              selectedDate={state.selectedDate}
              activeSelection={state.daySource === 'calendar'}
              onSelect={state.handleCalendarDay}
              onDoubleClick={inline ? undefined : (date) => {
                state.handleCalendarDay(date)
                onValueChange({ date, time: state.selectedTime || null, period: state.selectedPeriod })
                onClose()
              }}
            />
          </div>

          {/* Vertical separator */}
          <div className="when-dropdown__separator-vertical" />

          {/* Time Column */}
          <div className="when-dropdown__column when-dropdown__column--time">
            <div className="when-dropdown__column-label">
              {t('taskCreation.whenTimeLabel')}
            </div>

            <PeriodOptions
              selectedDate={state.selectedDate}
              selectedPeriod={state.selectedPeriod}
              selectedTime={state.selectedTime}
              timeSource={state.timeSource}
              onPeriodSelect={state.handlePeriodSelect}
              onValueChange={onValueChange}
              onClose={onClose}
            />

            <TimePointRow
              selectedTime={state.selectedTime}
              timeSource={state.timeSource}
              hasSelection={state.hasSelection}
              onTimeChange={state.setSelectedTime}
              onApplyTime={state.applyTimeValue}
              onClearTime={state.handleTimeClear}
              onConfirm={state.handleConfirm}
              onClose={onClose}
              inline={inline}
            />

            {/*
              **Nur, wo es eine Aufgabe gibt, die schlafen kann.** Bis zum
              19.09.2026 stand die Zeile überall und war überall tot („Kommt
              bald"); in der Neuanlage fehlt sie jetzt, statt ausgegraut zu sein.
            */}
            {onRest && (
              <div className="when-dropdown__rest-section">
                <div className="context-menu-content__separator" />
                <button
                  type="button"
                  className="when-dropdown__item"
                  onClick={() => { onRest(); onClose() }}
                >
                  <DormantIcon size={16} />
                  <span className="when-dropdown__item-text">
                    {t(resting ? 'taskCreation.whenWake' : 'taskCreation.whenResting')}
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <WhenDropdownFooter
        placement={placement}
        hasSelection={state.hasSelection}
        hasExistingDate={value != null}
        onConfirm={state.handleConfirm}
        onRemove={onRemove}
        onClose={onClose}
      />
    </div>
  )
}

// Re-export icons for use in TaskEntry badge
export { TodayIcon, TomorrowIcon, PeriodIcon } from './WhenDropdownIcons'
